import createCampaign from '../graphql/mutations/createCampaign';
import { HeadersWithoutApiKeyWorkaround } from './constants';
import queryMyCampaigns from '../graphql/queries/queryMyCampaigns';
import queryCampaign from '../graphql/queries/queryCampaign';
import updateCampaign from '../graphql/mutations/updateCampaign';
import deletePostMutation from '../graphql/mutations/deletePostMutation';
import updatePostMutation from '../graphql/mutations/updatePostMutation';
import queryCampaignPost from '../graphql/queries/queryCampaignPost';
import updatePostTranscript from '../graphql/mutations/updatePostTranscript';
import queryCampaignAnalytics from '../graphql/queries/queryCampaignAnalytics';
import { performImageUploadIfNeeded } from './imageAudioUploadHelper';
import queryCampaignPosts from '../graphql/queries/queryCampaignPosts';
import duplicateCampaign from '../graphql/mutations/duplicateCampaign';
import setCampaignTags from '~/graphql/mutations/setCampaignTags';

export const state = () => ({
    campaigns: null,
    post: null,
    campaignAnalytics: {},
    // for campaign edit
    campaignForEditing: undefined,
    // new campaign
    campaignForCreation: undefined,
});

function mapCampaign(campaign) {
    return {
        ...campaign,
        aiMetrics: JSON.parse(campaign.aiMetrics),
        widgetConfigs: JSON.parse(campaign.widgetConfigs),
    };
}

export const actions = {
    async setCampaignForCreation(context, campaign) {
        await context.commit('setCampaignForCreation', campaign);
    },
    async updateCampaignForCreation(context, { key, value }) {
        await context.commit('updateCampaignForCreation', { key, value });
    },

    async setCampaignForEditing(context, campaign) {
        await context.commit('setCampaignForEditing', { ...campaign });
    },
    async updateCampaignForEditing(context, { key, value }) {
        await context.commit('updateCampaignForEditing', { key, value });
    },

    async createCampaign(context, payload) {
        try {
            const campaign = await this.app.apolloProvider.defaultClient.mutate({
                mutation: createCampaign.createCampaignMutation,
                variables: payload,
                context: HeadersWithoutApiKeyWorkaround,
            });

            await context.commit('setCampaign', campaign.data.createCampaign);
            return campaign.data.createCampaign;
        } catch (e) {
            await context.dispatch('app/showServerError', {}, { root: true });
            console.error(e);
            throw e;
        }
    },

    async fetchCampaign(context, campaignID) {
        try {
            const campaignData = await this.app.apolloProvider.defaultClient.query({
                query: queryCampaign.campaign,
                variables: { campaignID: campaignID },
                context: HeadersWithoutApiKeyWorkaround,
            });

            await context.commit('setCampaign', mapCampaign(campaignData.data.campaign));
        } catch (e) {
            if (e.graphQLErrors?.[0]?.errorType === 'ErrorNotFound') {
                await context.dispatch('app/show404Error', {}, { root: true });
                console.warn(`received 404 for campaign ${campaignID}`);
            } else {
                await context.dispatch('app/showServerError', {}, { root: true });
                console.error(e);
            }
        }
    },

    async fetchCampaignPosts(context, { campaignID, offset, limit, searchQuery, clusterID, filters }) {
        const campaignPosts = await this.app.apolloProvider.defaultClient.query({
            query: queryCampaignPosts.campaignPosts,
            variables: { campaignID: campaignID, offset: offset, limit: limit, searchQuery: searchQuery, clusterID: clusterID, filters: filters },
            context: HeadersWithoutApiKeyWorkaround,
        });
        return { posts: campaignPosts.data.campaignPostsV2.posts, totalPostCount: campaignPosts.data.campaignPostsV2.totalPostCount };
    },

    async fetchCampaignAnalytics(context, campaignID) {
        try {
            const campaign = await this.app.apolloProvider.defaultClient.query({
                query: queryCampaignAnalytics.campaignAnalytics,
                variables: { campaignID: campaignID },
                context: HeadersWithoutApiKeyWorkaround,
            });

            await context.commit('setCampaignAnalytics', { id: campaignID, analytics: campaign.data.campaignAnalytics, funnel: campaign.data.analytics });
        } catch (e) {
            await context.dispatch('app/showServerError', {}, { root: true });
            console.error(e);
        }
    },

    async fetchMyCampaigns(context) {
        try {
            const campaign = await this.app.apolloProvider.defaultClient.query({
                query: queryMyCampaigns.myCampaigns,
                context: HeadersWithoutApiKeyWorkaround,
            });

            await context.commit('setCampaigns', campaign.data.myCampaigns.map(mapCampaign));
        } catch (e) {
            await context.dispatch('app/showServerError', {}, { root: true });
            console.error(e);
        }
    },

    async updateCampaign(context, { campaignID, payload, refreshCampaign }) {
        await this.$auth.refreshTokens();
        const image = await performImageUploadIfNeeded.bind(this)(payload.imageData);
        const backgroundImage = await performImageUploadIfNeeded.bind(this)(payload.backgroundImageData);

        // remove imageData, backgroundImageData, they are not needed in payload for update campaign
        delete payload.imageData;
        delete payload.backgroundImageData;

        if (image) {
            payload = { ...payload, image: { name: image.fileName } };
        }

        if (backgroundImage) {
            payload = { ...payload, backgroundImage: { name: backgroundImage.fileName } };
        }

        const campaign = await this.app.apolloProvider.defaultClient.mutate({
            mutation: updateCampaign.updateCampaignMutation,
            variables: {
                campaignID: campaignID,
                ...payload,
            },
            context: HeadersWithoutApiKeyWorkaround,
        });

        if (refreshCampaign) {
            await context.dispatch('fetchCampaign', campaign.data.updateCampaign.id);
        } else {
            await context.commit('setCampaign', campaign.data.updateCampaign);
        }
    },

    async deleteCampaignPost(context, { postID, campaignID }) {
        try {
            await this.app.apolloProvider.defaultClient.mutate({
                mutation: deletePostMutation.deletePostMutation,
                variables: { postId: postID },
                context: HeadersWithoutApiKeyWorkaround,
            });
        } catch (error) {
            console.error(error);
        }
    },

    async duplicateCampaign(context, { campaignID }) {
        const campaign = await this.app.apolloProvider.defaultClient.mutate({
            mutation: duplicateCampaign.duplicateCampaignMutation,
            variables: { campaignID: campaignID },
            context: HeadersWithoutApiKeyWorkaround,
        });

        return campaign.data.duplicateCampaign;
    },

    async changePostStatus(context, { campaign, post, isPublic }) {
        const response = await this.app.apolloProvider.defaultClient.mutate({
            mutation: updatePostMutation.updatePostMutation,
            variables: {
                postId: post.id,
                status: isPublic ? 'public' : 'private',
            },
            context: HeadersWithoutApiKeyWorkaround,
        });
        await context.commit('updateCampaignPost', { campaignID: campaign.id, post: response.data.updatePost });
    },

    async fetchPostWithFullTranscripts(context, postID) {
        try {
            const responseData = await this.app.apolloProvider.defaultClient.query({
                query: queryCampaignPost.campaignPost,
                context: HeadersWithoutApiKeyWorkaround,
                variables: { postID },
            });

            // We do this to make sure we have a deep copy we're allowed to modify (by parsing
            // and re-assigning aiMetrics) without stepping on references that might be held in
            // GraphQL's cache.
            const response = JSON.parse(JSON.stringify(responseData));
            response.data.campaignPost.aiMetrics = JSON.parse(response.data.campaignPost.aiMetrics);

            await context.commit('setPost', response.data.campaignPost);
        } catch (e) {
            await context.dispatch('app/showServerError', {}, { root: true });
            console.error(e);
        }
    },

    async updatePostTranscript(context, { post, words, campaignID }) {
        const response = await this.app.apolloProvider.defaultClient.mutate({
            mutation: updatePostTranscript.updatePostTranscript,
            variables: { postId: post.id, words: words },
            context: HeadersWithoutApiKeyWorkaround,
        });

        await context.commit('updateCampaignPost', { campaignID: campaignID, post: response.data.updatePostTranscript });
    },

    async setCampaignTags(context, { campaignID, newTags, oldTags }) {
        try {
            await context.commit('setCampaignTags', { campaignID: campaignID, tags: newTags });

            await this.app.apolloProvider.defaultClient.mutate({
                mutation: setCampaignTags.setCampaignTags,
                variables: { campaignID: campaignID, tagIDs: newTags.map((tag) => tag.id) },
                context: HeadersWithoutApiKeyWorkaround,
            });
        }  catch (e) {
            console.error(e);
            await context.commit('setCampaignTags', { campaignID: campaignID, tags: oldTags });
        }
    },
};

export const mutations = {
    setCampaignTags(state, { campaignID, tags }) {
        const newCampaigns = [];
        state.campaigns.forEach((campaign) => {
            let newCampaign = { ...campaign };
            if (newCampaign.id === campaignID) {
                newCampaign.tags = tags;
            }

            newCampaigns.push(newCampaign);
        })
        state.campaigns = newCampaigns;
    },
    setCampaignForCreation(state, campaign) {
        state.campaignForCreation = campaign;
    },
    updateCampaignForCreation(state, { key, value }) {
        state.campaignForCreation = { ...state.campaignForCreation, [key]: value };
    },

    setCampaignForEditing(state, campaign) {
        state.campaignForEditing = campaign;
    },
    updateCampaignForEditing(state, { key, value }) {
        state.campaignForEditing = { ...state.campaignForEditing, [key]: value };
    },

    setCampaigns(state, campaigns) {
        state.campaigns = campaigns;
    },

    setCampaign(state, campaign) {
        let updated = false;

        const newCampaigns = [];
        const oldCampaigns = state.campaigns ? state.campaigns : [];
        for (let i = 0; i < oldCampaigns.length; i++) {
            if (oldCampaigns[i].id === campaign.id) {
                // TODO: @simon is array approach the right one for this?
                // because the campaign was already in the campaigns we do not want to add it once more
                // at the end of the function
                updated = true;
                newCampaigns.unshift(campaign);
            } else {
                newCampaigns.unshift(oldCampaigns[i]);
            }
        }

        if (!updated) {
            newCampaigns.unshift(campaign);
        }

        state.campaigns = newCampaigns;
    },

    setPost(state, post) {
        state.post = post;
    },

    setCampaignAnalytics(state, { id, analytics, funnel }) {
        state.campaignAnalytics = { ...state.campaignAnalytics, [id]: { ...analytics, funnel: JSON.parse(funnel.funnel) } };
    },

    updateCampaignPost(state, { post }) {
        state.post = post;
    },
};

export const getters = {
    getCampaigns: (state) => {
        return state.campaigns;
    },

    getCampaignAnalytics: (state) => (campaignID) => {
        return state.campaignAnalytics[campaignID];
    },

    getPost(state) {
        return state.post;
    },

    getCampaignForEditing(state) {
        return state.campaignForEditing;
    },

    get: (state) => (campaignID) => {
        if (campaignID === null) {
            return null;
        }

        if (state.campaigns === null) {
            return null;
        }

        const filtered = state.campaigns.filter((campaign) => campaign.id === campaignID);
        if (filtered.length === 0) {
            return null;
        }

        return filtered[0];
    },
};
